import { API_BASE_URL, getAuthToken } from './apiService';

const delay = (milliseconds, signal) => new Promise((resolve) => {
  const timer = setTimeout(resolve, milliseconds);
  signal?.addEventListener('abort', () => {
    clearTimeout(timer);
    resolve();
  }, { once: true });
});

const parseEventBlock = (block) => {
  const lines = block.split(/\r?\n/);
  const parsed = { id: null, event: 'message', data: '' };
  lines.forEach((line) => {
    if (!line || line.startsWith(':')) return;
    const separator = line.indexOf(':');
    const key = separator >= 0 ? line.slice(0, separator) : line;
    const value = separator >= 0 ? line.slice(separator + 1).replace(/^ /, '') : '';
    if (key === 'id') parsed.id = value;
    if (key === 'event') parsed.event = value;
    if (key === 'data') parsed.data = parsed.data ? `${parsed.data}\n${value}` : value;
  });
  return parsed;
};

export const runJobProcessingStream = async ({
  signal,
  after = 0,
  onEvent,
  onConnectionChange
}) => {
  let lastEventId = Number(after) || 0;
  let retryDelay = 1000;

  while (!signal?.aborted) {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/jobs/imports/events/stream?after=${lastEventId}`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        cache: 'no-store',
        signal
      });
      if (!response.ok || !response.body) {
        throw new Error(`Job processing stream failed with status ${response.status}`);
      }

      onConnectionChange?.(true);
      retryDelay = 1000;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!signal?.aborted) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const event = parseEventBlock(block);
          if (event.id) lastEventId = Number(event.id) || lastEventId;
          if (event.event === 'job-processing' && event.data) {
            onEvent?.(JSON.parse(event.data));
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (error) {
      if (signal?.aborted || error?.name === 'AbortError') break;
      onConnectionChange?.(false, error);
    }

    onConnectionChange?.(false);
    if (!signal?.aborted) {
      await delay(retryDelay, signal);
      retryDelay = Math.min(15000, retryDelay * 2);
    }
  }

  return lastEventId;
};
